import { useRef, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Input } from 'almanac-ui/TextInputField/Input'
import Styles from './NewProjectModal.module.scss'
import { AlmanacFile, ViewType } from 'types'
import { FileTitle } from 'components/FilenameAndBreadcrumb/FilenameAndBreadcrumb'
import { ProjectCard } from 'components/BlazeCommandLine/components/ProjectCard'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreateFolder } from '../hooks/useCreateFolder'
import { useDetectKeyDown } from 'utils/hookUtils'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useCloseLeftSidebar } from 'hooks/useCloseLeftSidebar'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { Heading } from 'almanac-ui/Heading/Heading'
import { Calendar2, Menu6, Table2 } from 'blaze-ui/icons'

interface Props {
  parentFolder?: AlmanacFile | null
}

export const NewProjectModal = ({ parentFolder }: Props) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { closeAllModals, closeModal, stack } = useModals()
  const { isDesktop, isMobileMd, isMobile } = useResponsiveDesign()
  const [title, setTitle] = useState<string>('')
  const [viewType, setViewType] = useState<ViewType>(ViewType.Calendar)
  const { closeLeftSidebar } = useCloseLeftSidebar()
  const inputRef = useRef<HTMLDivElement>(null)

  const handleClose = () => {
    if (isMobile) closeLeftSidebar()
    closeAllModals()
  }

  const { createFolder, isCreating } = useCreateFolder({
    title,
    chosenWorkspaceId: currentWorkspaceId,
    handleClose: handleClose,
    defaultView: viewType,
    chosenParentFolder: parentFolder,
    databaseMode: viewType !== ViewType.List,
    handbookMode: false,
    handleError: () => {
      showErrorMessageToast('Please enter a title')
      inputRef.current && inputRef.current.querySelector('input')?.focus()
    },
  })

  useDetectKeyDown(['Enter'], createFolder)

  return (
    <Modal.Root size={isDesktop ? 'lg' : 'sm'} aria-label="New Project">
      <Modal.Header title="New Project" />

      <Modal.Content className={Styles.modalContent}>
        <div className={Styles.content}>
          <div className={Styles.contentInner}>
            <Heading level={4}>Project name</Heading>
            <div className={Styles.titleWrapper}>
              <div ref={inputRef} className={Styles.titleInput}>
                <Input
                  autoFocus={!isMobileMd}
                  aria-label="Title"
                  name="title"
                  size="lg"
                  placeholder="Untitled"
                  onChange={(event) => setTitle(event.currentTarget.value)}
                  value={title === 'Untitled' ? '' : title}
                />
              </div>
            </div>
            <Heading level={4}>Default view</Heading>
            <div className={Styles.cards}>
              <ProjectCard
                title="Calendar"
                description="Month and week views for event planning and scheduling"
                icon={Calendar2}
                color="var(--green-50)"
                isSelected={viewType === ViewType.Calendar}
                onClick={() => setViewType(ViewType.Calendar)}
                dataTestId="calendar-card"
              />
              <ProjectCard
                title="Table"
                description="View or store any kind of structured data"
                icon={Table2}
                color="var(--orange-50)"
                isSelected={viewType === ViewType.Table}
                onClick={() => setViewType(ViewType.Table)}
                dataTestId="table-card"
              />
              <ProjectCard
                title="List"
                description="Simple list of docs and their properties"
                icon={Menu6}
                color="var(--magenta-50)"
                isSelected={viewType === ViewType.List}
                onClick={() => setViewType(ViewType.List)}
                dataTestId="list-card"
              />
            </div>
          </div>
          {isDesktop && (
            <div className={Styles.previewContainer}>
              <div className={Styles.previewTitleContainer}>
                <FileTitle title={title || 'Untitled'} color={!title ? 'var(--neutral-30)' : undefined} />
              </div>
              <Preview type={viewType} />
            </div>
          )}
        </div>
      </Modal.Content>

      <Modal.Footer className={Styles.footer}>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} size="lg" variant="tertiary" data-testid="back-btn">
            {stack.length > 1 ? 'Back' : 'Cancel'}
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton size="lg" variant="primary" onPress={createFolder} isDisabled={isCreating || !title}>
            Create Project
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}

const Preview = ({ type }: { type: ViewType }) => {
  switch (type) {
    case ViewType.List:
      return (
        <img
          src="https://res.cloudinary.com/almanac/image/upload/v1701886658/new_project_modal/bmrhgxs8c5djcjqer2ie.png"
          alt="Folder view"
        />
      )
    case ViewType.Board:
      return (
        <img
          src="https://res.cloudinary.com/almanac/image/upload/v1695387468/new_project_modal/Board_wpxtic.png"
          alt="Board view"
        />
      )
    case ViewType.Calendar:
      return (
        <img
          src="https://res.cloudinary.com/almanac/image/upload/v1695387468/new_project_modal/Calendar_riarqh.png"
          alt="Calendar view"
        />
      )
    case ViewType.Table:
    default:
      return (
        <img
          src="https://res.cloudinary.com/almanac/image/upload/v1695387470/new_project_modal/Database_imo543.png"
          alt="Table view"
        />
      )
  }
}
